import React, { useMemo } from 'react';

import { useLocation, useNavigate } from 'react-router-dom';
import { lensTokens } from '~/configs/unifiedAnalyticsTheme';
import { type NativePrintProps, NativeReportShell } from '~/nativeLenses/NativePrintBoard';
import { nativeLensId } from '~/nativeLenses/nativeLensId';

import { Box, Button, CircularProgress, Tab, Tabs, Typography } from '@mui/material';

import { FONT_STACK_SUISSE_INTL } from '~/components/brand/brandTokens';

import PageChatButton from '~/pages/AskRichardUnified/page/PageChatButton';
import { usePageBridge } from '~/pages/AskRichardUnified/page/pageBridge';
import type { PageField } from '~/pages/AskRichardUnified/page/pageContract';
import { validatePageFilters } from '~/pages/AskRichardUnified/page/pageFilters';

import { useGetReportingLeLensData } from '~/hooks/apis/reporting/useGetReportingLeLensData';

import { download } from '../RmMonitor/liveModel';

import {
  PAGE_ID,
  TOP_METRICS,
  VIEWS,
  aiv,
  headlineForBrand,
  attachment,
  byMetric,
  dailyCsv,
  dailyForBrand,
  filterName,
  formatMetric,
  hotelCsv,
  integer,
  itemsAr,
  metricValue,
  money0,
  money2,
  namedCsv,
  offerCsv,
  percent1,
  periodCsv,
  readDaily,
  readHotels,
  readNamed,
  readOffers,
  readViewsOverTime,
  readVendorSlices,
  totalsForVendor,
  readPeriods,
  readTotals,
  readViews,
  readWbr,
  refundRate,
  sumViews,
  toggleSelection,
  viewRates,
  wbrCsv,
  wbrFrames,
  type NamedRow,
  type TopMetric,
  type ViewRow,
} from './liveDataAdapter';
import { offerExportAvailability, trafficChannelLabel } from './offerViewPresentation';

const CADENCES = ['Daily', 'Weekly', 'Monthly', 'Quarterly', 'Yearly'] as const;
type Cadence = (typeof CADENCES)[number];

function bucketDate(day: string, cadence: Cadence): string {
  const date = new Date(`${day}T00:00:00Z`);
  if (cadence === 'Weekly') date.setUTCDate(date.getUTCDate() - (date.getUTCDay() + 6) % 7);
  if (cadence === 'Monthly' || cadence === 'Quarterly' || cadence === 'Yearly') date.setUTCDate(1);
  if (cadence === 'Quarterly') date.setUTCMonth(Math.floor(date.getUTCMonth() / 3) * 3);
  if (cadence === 'Yearly') date.setUTCMonth(0);
  return date.toISOString().slice(0, 10);
}

const SCHEMA: Record<string, PageField> = {
  view: { type: 'enum', values: [...VIEWS] },
  from: { type: 'date' },
  to: { type: 'date' },
  cadence: { type: 'enum', values: [...CADENCES] },
  metric: { type: 'enum', values: [...TOP_METRICS] },
  brand: { type: 'text', maxLength: 300 },
  vendor: { type: 'text', maxLength: 300 },
  deal: { type: 'text', maxLength: 300 },
  concept: { type: 'text', maxLength: 300 },
  product: { type: 'text', maxLength: 300 },
};

function rowsOf(cards: Array<{ cardId: string; rows?: Array<Record<string, unknown>>; status: string }> | undefined, id: string) {
  return (cards?.find((card) => card.cardId === id)?.rows ?? []) as Record<string, unknown>[];
}

function Bars({ rows, metric, selected, onSelect, limit, dense }: { rows: readonly NamedRow[]; metric: TopMetric; selected: string | null; onSelect: (name: string) => void; limit?: number; dense?: boolean }) {
  const peak = Math.max(1, ...rows.map((row) => Math.abs(metricValue(row, metric))));
  const drawn = limit === undefined ? rows : rows.slice(0, limit);
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: dense ? 0.25 : 0.75 }}>
      {drawn.map((row) => (
        <Box key={row.name || '(blank)'} onClick={() => onSelect(row.name)} sx={{ cursor: 'pointer' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
            <Typography sx={{ fontSize: dense ? 11 : 13, fontWeight: selected === row.name ? 700 : 500 }}>{row.name || '(blank)'}</Typography>
            <Typography sx={{ fontSize: dense ? 11 : 13 }}>{formatMetric(metricValue(row, metric), metric)}</Typography>
          </Box>
          <Box sx={{ height: dense ? 5 : 8, bgcolor: 'action.hover', borderRadius: 4 }}>
            <Box sx={{ width: `${(Math.abs(metricValue(row, metric)) / peak) * 100}%`, height: dense ? 5 : 8, bgcolor: (theme) => selected && selected !== row.name ? theme.palette.text.disabled : lensTokens(theme).accent, borderRadius: 4 }} />
          </Box>
        </Box>
      ))}
    </Box>
  );
}

function DailyBars({ rows }: { rows: { day: string; ttvAr: number; gmAr: number; ttvBr: number }[] }) {
  const bars = rows.map((row) => ({ name: row.day, ttvAr: row.ttvAr, gmAr: row.gmAr, ttvBr: row.ttvBr, gmBr: 0, itemsBr: 0, itemsR: 0, attachNum: 0, attachDen: 0 }));
  const mid = Math.ceil(bars.length / 2);
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, columnGap: 3 }}>
      <Bars dense rows={bars.slice(0, mid)} metric="TTV (AR)" selected={null} onSelect={() => undefined} />
      <Bars dense rows={bars.slice(mid)} metric="TTV (AR)" selected={null} onSelect={() => undefined} />
    </Box>
  );
}

function Kpi({ label, value }: { label: string; value: string }) {
  return (
    <Box sx={{ flex: '1 1 140px', bgcolor: 'background.paper', borderRadius: 1, px: 1.5, py: 1.25 }}>
      <Typography sx={{ fontSize: 12, letterSpacing: 0.2, color: 'text.secondary' }}>{label}</Typography>
      <Typography sx={{ fontSize: 22, fontWeight: 650, color: 'text.primary' }}>{value}</Typography>
    </Box>
  );
}

function Export({ name, content, disabled }: { name: string; content: string; disabled: boolean }) {
  return (
    <Button variant="outlined" size="small" disabled={disabled} onClick={() => download(`${name}.csv`, content)}>
      Export CSV
    </Button>
  );
}

function defaultDates(view: string): { from: string; to: string } {
  const today = new Intl.DateTimeFormat('en-CA', { timeZone: 'Australia/Sydney', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
  const [year, month] = today.split('-').map(Number);
  const monthsBack = view === 'Sales Overview' || view === 'Region Trends' || view === 'Top Performance' ? 1 : 2;
  return {
    from: new Date(Date.UTC(year, month - 1 - monthsBack, 1)).toISOString().slice(0, 10),
    to: new Date(Date.UTC(year, month, 0)).toISOString().slice(0, 10),
  };
}

function validDate(value: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(value) && !Number.isNaN(Date.parse(`${value}T00:00:00Z`)) && new Date(`${value}T00:00:00Z`).toISOString().slice(0, 10) === value;
}

function ExperienceDashContent() {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const requested = params.get('view');
  const view = VIEWS.includes(requested as (typeof VIEWS)[number]) ? (requested as (typeof VIEWS)[number]) : VIEWS[0];
  const datedView = view !== 'Period Comparison' && view !== 'WBR summary';
  const defaults = defaultDates(view);
  const from = params.get('from') || defaults.from;
  const to = params.get('to') || defaults.to;
  const validRange = validDate(from) && validDate(to) && from <= to;
  const metric = TOP_METRICS.includes(params.get('metric') as TopMetric) ? (params.get('metric') as TopMetric) : 'GM (AR)';
  const cadence = CADENCES.includes(params.get('cadence') as Cadence) ? params.get('cadence') as Cadence : 'Daily';
  const brand = params.get('brand');
  const vendor = params.get('vendor');
  const deal = params.get('deal');
  const concept = params.get('concept');
  const product = params.get('product');
  const currentCards = {
    'Sales Overview': ['sales-totals', 'sales-daily', 'sales-brand'],
    'Region Trends': ['sales-country'],
    'Top Performance': ['sales-vendor', 'sales-deal', 'sales-concept'],
    'Offer Views': ['views-product', 'views-traffic', 'views-offer', 'views-over-time'],
    'Hotel In-flow or Bundle': ['hotel-inflow'],
    'Period Comparison': ['period-comparison'],
    'WBR summary': ['wbr'],
  }[view];
  const query = useGetReportingLeLensData(datedView && !validRange ? undefined : nativeLensId(PAGE_ID), { ...(datedView ? { period: { window: { from, to }, comparison: null } } : {}), ...(view === 'Top Performance' && product ? { product: [product] } : {}) }, false, undefined, undefined, undefined, currentCards);
  const cards = query.data?.cards;
  const ready = (!datedView || validRange) && !query.isLoading && !query.isError && query.responseIdentity === query.requestIdentity;
  const bridgeReady = ready && query.responseIdentity === query.requestIdentity && currentCards.every((id) => cards?.some((card) => card.cardId === id && (card.status === 'ready' || card.status === 'empty')));
  const problem = cards?.find((card) => card.status === 'error');

  const totals = useMemo(() => readTotals(rowsOf(cards, 'sales-totals')), [cards]);
  const daily = useMemo(() => readDaily(rowsOf(cards, 'sales-daily')), [cards]);
  const countries = useMemo(() => readNamed(rowsOf(cards, 'sales-country')), [cards]);
  const brands = useMemo(() => readNamed(rowsOf(cards, 'sales-brand')), [cards]);
  const vendors = useMemo(() => readNamed(rowsOf(cards, 'sales-vendor')), [cards]);
  const deals = useMemo(() => readVendorSlices(rowsOf(cards, 'sales-deal')), [cards]);
  const concepts = useMemo(() => readVendorSlices(rowsOf(cards, 'sales-concept')), [cards]);
  const products = useMemo(() => readViews(rowsOf(cards, 'views-product')), [cards]);
  const traffic = useMemo(() => readViews(rowsOf(cards, 'views-traffic')), [cards]);
  const offers = useMemo(() => readOffers(rowsOf(cards, 'views-offer')), [cards]);
  const viewsOverTime = useMemo(() => readViewsOverTime(rowsOf(cards, 'views-over-time')), [cards]);
  const hotels = useMemo(() => readHotels(rowsOf(cards, 'hotel-inflow')), [cards]);
  const periods = useMemo(() => readPeriods(rowsOf(cards, 'period-comparison')), [cards]);
  const wbr = useMemo(() => readWbr(rowsOf(cards, 'wbr')), [cards]);

  const shownBrands = filterName(brands, brand);
  const shownDaily = dailyForBrand(daily, brand, from, to);
  const salesBuckets = new Map<string, typeof shownDaily[number]>();
  for (const row of shownDaily) {
    const day = bucketDate(row.day, cadence);
    const prior = salesBuckets.get(day);
    if (prior) { prior.ttvAr += row.ttvAr; prior.gmAr += row.gmAr; prior.ttvBr += row.ttvBr; prior.refunds += row.refunds; }
    else salesBuckets.set(day, { ...row, day });
  }
  const viewBuckets = new Map<string, typeof viewsOverTime[number]>();
  for (const row of viewsOverTime) {
    const day = bucketDate(row.day, cadence);
    const key = JSON.stringify([day, row.channel]);
    const prior = viewBuckets.get(key);
    if (prior) { prior.gm += row.gm; prior.uniqueViews += row.uniqueViews; prior.mpv = prior.uniqueViews ? prior.gm / prior.uniqueViews : 0; }
    else viewBuckets.set(key, { ...row, day });
  }
  const salesHeadline = headlineForBrand(totals ?? { ttvAr: 0, gmAr: 0, ttvBr: 0, gmBr: 0, refunds: 0, itemsBr: 0, itemsR: 0, attachNum: 0, attachDen: 0 }, brand ? (brands.find((row) => row.name === brand) ?? null) : null);
  const shownVendors = filterName(vendors, vendor);
  const shownDeals = filterName(totalsForVendor(deals, vendor), deal);
  const shownConcepts = filterName(totalsForVendor(concepts, vendor), concept);
  const shownOffers = product ? offers.filter((row) => row.productName === product) : offers;
  const shownProducts = product ? products.filter((row) => row.name === product) : products;
  const viewTotals = sumViews(shownProducts);
  const rates = viewRates(viewTotals);
  const offerExport = offerExportAvailability(cards?.find((card) => card.cardId === 'views-offer'));
  const usable = (id: string) => {
    const card = cards?.find((item) => item.cardId === id);
    return Boolean(card && (card.status === 'ready' || card.status === 'empty') && !card.truncated);
  };
  const scope = currentCards.flatMap((id) => rowsOf(cards, id)).find((row) => row.scope_from && row.scope_to);
  const scopeDate = (value: unknown) => new Date(`${String(value)}T00:00:00Z`).toLocaleDateString('en-AU', {
    day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC',
  });
  const windowLabel = scope
    ? scope.scope_from === scope.scope_to ? scopeDate(scope.scope_from) : `${scopeDate(scope.scope_from)}–${scopeDate(scope.scope_to)}`
    : query.data?.period?.label.current ?? 'Selected period';
  const currentWindow = view === 'Period Comparison'
    ? `${rowsOf(cards, 'period-comparison').map((row) => `${row.period}: ${scopeDate(row.scope_from)}–${scopeDate(row.scope_to)}`).join('; ')}, before refunds`
    : view === 'WBR summary' ? '12 Week Avg, Last Week and Week Prior from WBR_Experience'
      : `${windowLabel} Sydney${view === 'Top Performance' && product ? ` · Product: ${product}` : ''}`;

  const fields: Record<string, PageField> = { view: SCHEMA.view };
  if (datedView) Object.assign(fields, { from: SCHEMA.from, to: SCHEMA.to });
  if (view === 'Sales Overview' || view === 'Offer Views') fields.cadence = SCHEMA.cadence;
  if (view === 'Sales Overview') fields.brand = SCHEMA.brand;
  if (view === 'Region Trends' || view === 'Top Performance') fields.metric = SCHEMA.metric;
  if (view === 'Top Performance') Object.assign(fields, { vendor: SCHEMA.vendor, deal: SCHEMA.deal, concept: SCHEMA.concept });
  if (view === 'Offer Views') fields.product = SCHEMA.product;

  const replace = (next: URLSearchParams) => navigate({ pathname: location.pathname, search: next.toString() }, { replace: true });
  const setParam = (key: string, value: string | null) => {
    const next = new URLSearchParams(window.location.search);
    if (value) next.set(key, value);
    else next.delete(key);
    replace(next);
  };

  usePageBridge({
    pageId: PAGE_ID,
    view: {
      title: `Experience Dash — ${view}`,
      ready: bridgeReady,
      filterSchema: fields,
      filters: { view, ...(datedView ? { from, to } : {}), ...(view === 'Sales Overview' || view === 'Offer Views' ? { cadence } : {}), ...(view === 'Sales Overview' ? { brand: brand ?? '' } : {}), ...(view === 'Region Trends' || view === 'Top Performance' ? { metric } : {}), ...(view === 'Top Performance' ? { vendor: vendor ?? '', deal: deal ?? '', concept: concept ?? '' } : {}), ...(view === 'Offer Views' ? { product: product ?? '' } : {}) },
      dataMode: 'live BigQuery through Lens',
      summary: {
        worksheet: view,
        window: currentWindow,
        salesHeadline: view === 'Sales Overview' ? {
          ttvAr: salesHeadline.ttvAr,
          gmAr: salesHeadline.gmAr,
          itemsAr: itemsAr(salesHeadline.itemsBr, salesHeadline.itemsR),
          aivAr: aiv(salesHeadline.ttvAr, itemsAr(salesHeadline.itemsBr, salesHeadline.itemsR)) ?? 0,
          refundAmountPercent: percent1(refundRate(salesHeadline.refunds, salesHeadline.ttvBr)),
          hotelAttachmentPercent: percent1(attachment(salesHeadline.attachNum, salesHeadline.attachDen)),
        } : undefined,
        rows: currentCards.reduce((count, id) => count + rowsOf(cards, id).length, 0),
        activeFilters: view === 'Sales Overview' ? { brand }
          : view === 'Region Trends' ? { metric }
            : view === 'Top Performance' ? { metric, vendor, deal, concept, product }
              : view === 'Offer Views' ? { product } : {},
        offerViews: view === 'Offer Views' ? {
          uniqueViews: viewTotals.uniqueViews,
          totalViews: viewTotals.totalViews,
          mpv: rates.mpv,
          offersShown: Math.min(shownOffers.length, 20),
          trafficScope: 'All products. Product selection does not apply to Traffic or Views Over Time.',
        } : undefined,
        complete: currentCards.every(usable),
      },
      definitions: {
        ttvAr: 'TTV (AR) is gross_sales_adjusted_aud minus accounting_refund_amount_adjusted_aud on category experience.',
        itemsAr: 'Items (AR) is the distinct sale item count minus the distinct refund item count. Those counts are already COUNT DISTINCT, then subtracted.',
        attachment: 'Hotel attachment uses every hotel order as the denominator. The experience category filter is inside the numerator only, because the Attach sheet does not filter category.',
        uniqueViews: 'Unique Views sums unique_view_count. That column is already a distinct count on each views_with_sales row.',
        productScope: 'On Top Performance, product selection filters vendors, deals and concepts before aggregation. On Offer Views, product selection filters the headline totals and Offers table only. Traffic and Views Over Time remain across all products because those source datasets have no product dimension.',
      },
    },
    edit: (changes) => {
      if (!bridgeReady) throw new Error('Page data is not ready.');
      validatePageFilters(changes, fields);
      const options = { brand: brands, vendor: vendors, deal: totalsForVendor(deals, vendor), concept: totalsForVendor(concepts, vendor), product: products };
      for (const [key, rows] of Object.entries(options)) {
        const value = changes[key];
        if (value && !rows.some((row) => row.name === value)) throw new Error(`Invalid ${key}: select a value listed on the page.`);
      }
      if ('view' in changes && changes.view !== view && Object.keys(changes).length > 1) throw new Error('Change the view first, then select its filters.');
      const nextFrom = String(changes.from ?? from);
      const nextTo = String(changes.to ?? to);
      if (datedView && (!validDate(nextFrom) || !validDate(nextTo) || nextFrom > nextTo)) throw new Error('Choose a valid date range.');
      const next = new URLSearchParams(location.search);
      if ('from' in changes) next.set('from', nextFrom);
      if ('to' in changes) next.set('to', nextTo);
      if ('view' in changes) next.set('view', String(changes.view));
      if ('metric' in changes) next.set('metric', String(changes.metric));
      if ('cadence' in changes) next.set('cadence', String(changes.cadence));
      for (const key of ['brand', 'vendor', 'deal', 'concept', 'product'] as const) {
        if (key in changes) (changes[key] ? next.set(key, String(changes[key])) : next.delete(key));
      }
      replace(next);
    },
  });

  return (
    <Box sx={{ px: { xs: 2, sm: 3 }, py: 2, maxWidth: 1280, minWidth: 0, color: 'text.primary', fontFamily: FONT_STACK_SUISSE_INTL, '& :where(button, input, select, textarea, table, th, td)': { fontFamily: 'inherit' } }}>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'flex-start' }, justifyContent: 'space-between', gap: 1.5, mb: 1.5 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography component="h1" sx={{ fontFamily: FONT_STACK_SUISSE_INTL, fontSize: { xs: 22, sm: 24 }, lineHeight: 1.3, fontWeight: 650, color: 'text.primary' }}>Experience Dash</Typography>
          <Typography sx={{ fontSize: 13, color: 'text.secondary', mt: 0.5 }}>{view}, {currentWindow}. Figures are after refunds unless a tile says otherwise.</Typography>
        </Box>
        <PageChatButton />
      </Box>
      <Tabs value={view} onChange={(_event, value) => setParam('view', value)} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
        {VIEWS.map((label) => (
          <Tab key={label} value={label} label={label} />
        ))}
      </Tabs>
      {datedView && (
        <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <label>From <input aria-label="From" type="date" value={from} onChange={(event) => setParam('from', event.target.value)} /></label>
          <label>To <input aria-label="To" type="date" value={to} onChange={(event) => setParam('to', event.target.value)} /></label>
          {!validRange && <Typography>Choose a valid date range.</Typography>}
        </Box>
      )}
      {query.isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}
      {query.isError && <Typography>This view could not be read. Try the page again.</Typography>}
      {problem && <Typography>One figure could not be read. The rest of the page is unchanged.</Typography>}
      {ready && view === 'Sales Overview' && totals && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Kpi label="TTV (AR)" value={money0(salesHeadline.ttvAr)} />
            <Kpi label="GM (AR)" value={money0(salesHeadline.gmAr)} />
            <Kpi label="Items (AR)" value={integer(itemsAr(salesHeadline.itemsBr, salesHeadline.itemsR))} />
            <Kpi label="AIV (AR)" value={money2(aiv(salesHeadline.ttvAr, itemsAr(salesHeadline.itemsBr, salesHeadline.itemsR)) ?? 0)} />
            <Kpi label="Refund Amount %" value={percent1(refundRate(salesHeadline.refunds, salesHeadline.ttvBr))} />
            <Kpi label="Hotel Attachment (BR)" value={percent1(attachment(salesHeadline.attachNum, salesHeadline.attachDen))} />
          </Box>
          <Box>
            <CadenceSelect cadence={cadence} onChange={(value) => setParam('cadence', value)} />
            <Typography sx={{ fontWeight: 650, mb: 1 }}>{cadence} TTV (AR)</Typography>
            <DailyBars rows={[...salesBuckets.values()]} />
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography sx={{ fontWeight: 650 }}>Brands</Typography>
            <Export name="experience-dash-sales-overview" content={dailyCsv(shownDaily)} disabled={!usable('sales-daily')} />
          </Box>
          <Bars limit={12} rows={[...shownBrands].sort((a, b) => a.name.localeCompare(b.name))} metric="TTV (AR)" selected={brand} onSelect={(name) => setParam('brand', toggleSelection(brand, name))} />
        </Box>
      )}
      {ready && view === 'Region Trends' && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography sx={{ fontWeight: 650 }}>Offer country by {metric}</Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <MetricSelect metric={metric} onChange={(value) => setParam('metric', value)} />
              <Export name="experience-dash-region-trends" content={namedCsv(byMetric(countries, metric), metric)} disabled={!usable('sales-country')} />
            </Box>
          </Box>
          <Bars rows={byMetric(countries, metric)} metric={metric} selected={null} onSelect={() => undefined} />
        </Box>
      )}
      {ready && view === 'Top Performance' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <MetricSelect metric={metric} onChange={(value) => setParam('metric', value)} />
            <Export name="experience-dash-top-performance" content={namedCsv(byMetric(shownVendors, metric), metric)} disabled={!usable('sales-vendor')} />
          </Box>
          <Typography sx={{ fontWeight: 650 }}>Vendors by {metric}</Typography>
          <Bars limit={12} rows={byMetric(shownVendors, metric)} metric={metric} selected={vendor} onSelect={(name) => setParam('vendor', toggleSelection(vendor, name))} />
          <Typography sx={{ fontWeight: 650 }}>Deals</Typography>
          <Bars limit={12} rows={byMetric(shownDeals, metric)} metric={metric} selected={deal} onSelect={(name) => setParam('deal', toggleSelection(deal, name))} />
          <Typography sx={{ fontWeight: 650 }}>Concepts</Typography>
          <Bars limit={12} rows={byMetric(shownConcepts, metric)} metric={metric} selected={concept} onSelect={(name) => setParam('concept', toggleSelection(concept, name))} />
        </Box>
      )}
      {ready && view === 'Offer Views' && (
        <OfferView windowLabel={currentWindow} products={shownProducts} traffic={traffic} offers={shownOffers} totals={viewTotals} rates={rates} product={product} overTime={[...viewBuckets.values()].sort((a, b) => a.day.localeCompare(b.day) || b.mpv - a.mpv || a.channel.localeCompare(b.channel))} cadence={cadence} onCadence={(value) => setParam('cadence', value)} exportable={offerExport.enabled} exportNotice={offerExport.reason} onProduct={(name) => setParam('product', toggleSelection(product, name))} />
      )}
      {ready && view === 'Hotel In-flow or Bundle' && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography sx={{ fontWeight: 650 }}>Hotel and experience pairs, {currentWindow}</Typography>
            <Export name="experience-dash-hotel-inflow" content={hotelCsv(hotels)} disabled={!usable('hotel-inflow')} />
          </Box>
          <SimpleTable headers={['Experience offer', 'Hotel vendor', 'Campaign', 'TTV (AR)', 'Items (AR)']} rows={hotels.slice(0, 25).map((row) => [row.experienceOffer, row.hotelVendor, row.campaign, money0(row.ttvAr), integer(row.itemsAr)])} />
        </Box>
      )}
      {ready && view === 'Period Comparison' && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography sx={{ fontWeight: 650 }}>September 2026 against September 2025, before refunds</Typography>
            <Export name="experience-dash-period-comparison" content={periodCsv(periods)} disabled={!usable('period-comparison')} />
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {periods.map((row) => (
              <Box key={row.period} sx={{ flex: '1 1 240px', bgcolor: 'background.paper', borderRadius: 1, p: 1.5 }}>
                <Typography sx={{ fontWeight: 650, mb: 1 }}>{row.period}</Typography>
                <Typography>TTV (BR) {money0(row.ttvBr)}</Typography>
                <Typography>GM (BR) {money0(row.gmBr)}</Typography>
                <Typography>Orders (BR) {integer(row.ordersBr)}</Typography>
                <Typography>Items (BR) {integer(row.itemsBr)}</Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}
      {ready && view === 'WBR summary' && <Wbr rows={wbr} exportable={usable('wbr')} />}
    </Box>
  );
}

function CadenceSelect({ cadence, onChange }: { cadence: Cadence; onChange: (value: Cadence) => void }) {
  return (
    <Box component="label" sx={{ fontSize: 13, display: 'flex', gap: 1, alignItems: 'center', '& select': { color: 'text.primary', bgcolor: 'background.paper', borderColor: 'divider', colorScheme: (theme) => theme.palette.mode } }}>
      Date cadence
      <select aria-label="Date cadence" value={cadence} onChange={(event) => onChange(event.target.value as Cadence)}>{CADENCES.map((value) => <option key={value} value={value}>{value}</option>)}</select>
    </Box>
  );
}

function MetricSelect({ metric, onChange }: { metric: TopMetric; onChange: (metric: TopMetric) => void }) {
  return (
    <Box component="label" sx={{ fontSize: 13, display: 'flex', gap: 1, alignItems: 'center', '& select': { color: 'text.primary', bgcolor: 'background.paper', borderColor: 'divider', colorScheme: (theme) => theme.palette.mode } }}>
      Top metric
      <select aria-label="Top metric" value={metric} onChange={(event) => onChange(event.target.value as TopMetric)}>
        {TOP_METRICS.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    </Box>
  );
}

function SimpleTable({ headers, rows }: { headers: string[]; rows: string[][] }) {
  return (
    <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, color: 'text.primary' }}>
      <thead>
        <tr>
          {headers.map((header) => (
            <Box component="th" key={header} sx={{ textAlign: 'left', px: 1, py: 0.75, borderBottom: 1, borderColor: 'divider', color: 'text.primary' }}>
              {header}
            </Box>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, index) => (
          <tr key={index}>
            {row.map((cell, cellIndex) => (
              <Box component="td" key={cellIndex} sx={{ px: 1, py: 0.75, borderBottom: 1, borderColor: 'divider' }}>
                {cell}
              </Box>
            ))}
          </tr>
        ))}
      </tbody>
    </Box>
  );
}

function OfferView({
  windowLabel,
  products,
  traffic,
  offers,
  totals,
  rates,
  product,
  overTime,
  cadence,
  onCadence,
  exportable,
  exportNotice,
  onProduct,
}: {
  windowLabel: string;
  products: ViewRow[];
  traffic: ViewRow[];
  offers: ReturnType<typeof readOffers>;
  totals: ViewRow;
  rates: ReturnType<typeof viewRates>;
  product: string | null;
  overTime: ReturnType<typeof readViewsOverTime>;
  cadence: Cadence;
  onCadence: (value: Cadence) => void;
  exportable: boolean;
  exportNotice?: string;
  onProduct: (name: string) => void;
}) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>Experience, {windowLabel}</Typography>
      <Typography variant="body2" color="text.secondary">
        {product ? `Selected product: ${product}. Select it again to show all products.` : 'All products. Select a product to filter these totals and the Offers table.'}
      </Typography>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Kpi label="Unique Views" value={integer(totals.uniqueViews)} />
        <Kpi label="Total Views" value={integer(totals.totalViews)} />
        <Kpi label="MpV" value={rates.mpv === null ? '' : money2(rates.mpv)} />
        <Kpi label="CR" value={rates.cr === null ? '' : `${rates.cr.toFixed(2)}%`} />
        <Kpi label="ATC%" value={rates.atc === null ? '' : `${rates.atc.toFixed(2)}%`} />
      </Box>
      <Typography sx={{ fontWeight: 650 }}>By product</Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {[...products].sort((a, b) => a.name.localeCompare(b.name)).map((row) => (
          <Button key={row.name || '(blank)'} data-lens-pdf-keep="" onClick={() => onProduct(row.name)} sx={{ justifyContent: 'space-between', color: 'text.primary', fontWeight: product === row.name ? 700 : 500 }}>
            <span>{row.name || '(blank)'}</span>
            <span>{integer(row.uniqueViews)}</span>
          </Button>
        ))}
      </Box>
      <Typography sx={{ fontWeight: 650 }}>Traffic</Typography>
      <Typography variant="body2" color="text.secondary">All products. Product selection does not apply to this traffic breakdown.</Typography>
      <SimpleTable headers={['Channel', 'Unique Views', 'TTV']} rows={[...traffic].sort((a, b) => b.uniqueViews - a.uniqueViews || a.name.localeCompare(b.name)).slice(0, 12).map((row) => [trafficChannelLabel(row.name), integer(row.uniqueViews), money0(row.ttv)])} />
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Typography sx={{ fontWeight: 650 }}>Offers</Typography>
        <Export name="experience-dash-offer-views" content={offerCsv(offers)} disabled={!exportable} />
      </Box>
      <Typography variant="body2" color="text.secondary">
        Showing {integer(Math.min(offers.length, 20))} offers from the returned results, lowest MpV first. MpV is margin divided by unique views.
      </Typography>
      {exportNotice && <Typography variant="body2" color="text.secondary" role="status">{exportNotice}</Typography>}
      <SimpleTable headers={['Offer', 'Product', 'MpV', 'Unique Views']} rows={offers.slice(0, 20).map((row) => [row.offerName, row.productName, money2(row.uniqueViews === 0 ? 0 : row.gm / row.uniqueViews), integer(row.uniqueViews)])} />
      <Typography sx={{ fontWeight: 650 }}>Views Over Time</Typography>
      <CadenceSelect cadence={cadence} onChange={onCadence} />
      <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>{cadence} MpV by traffic channel, {windowLabel}. All products; product selection does not apply.</Typography>
      <Box sx={{ maxHeight: 420, overflow: 'auto' }}>
        <SimpleTable headers={['View date', 'Traffic channel', 'MpV']} rows={overTime.map((row) => [row.day, trafficChannelLabel(row.channel), money2(row.mpv)])} />
      </Box>
      </Box>
  );
}

function Wbr({ rows, exportable }: { rows: ReturnType<typeof readWbr>; exportable: boolean }) {
  const origins = [...new Set(rows.map((row) => row.origin))].sort((a, b) => a.localeCompare(b));
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
        <Typography sx={{ fontWeight: 650 }}>TTV by purchase origin</Typography>
        <Export name="experience-dash-wbr" content={wbrCsv(rows)} disabled={!exportable} />
      </Box>
      <SimpleTable
        headers={['Experience', ...wbrFrames()]}
        rows={origins.map((origin) => [
          origin,
          ...wbrFrames().map((frame) => money0(rows.find((row) => row.origin === origin && row.timeFrame === frame)?.ttv ?? 0)),
        ])}
      />
    </Box>
  );
}

export default function ExperienceDashPage({ printTarget }: NativePrintProps) {
  return (
    <NativeReportShell
      pageId="experience-dash"
      title="Experience Dash"
      printTarget={printTarget}
      analytics
    >
      <ExperienceDashContent />
    </NativeReportShell>
  );
}
